/**
 * The owner's form, described to an agent.
 *
 * A jobsite's contact form is whatever its owner built, so "name, email, phone,
 * message" is only the shape of a form nobody has edited. Hard-coding those
 * keys submits leads that silently lose every field the owner asked for — the
 * server drops what the form doesn't declare. So the schema is generated from
 * the same `FieldDef` list the rendered form is built from.
 */

import type { FieldDef, FieldValue } from '../forms/form-data';
import { CORE_FIELD_KEYS } from '../forms/form-data';

/** One form field, paired with the JSON Schema property an agent sees. */
interface FormToolField {
  /** Property name in the tool's inputSchema. */
  property: string;
  field: FieldDef;
}

/** An image field is a file upload, which an agent can't perform. Omitted, so a
 *  required one is refused by name rather than silently half-filled. */
const AGENT_FILLABLE = (field: FieldDef) => field.type !== "image",

  /** "Gate code" → gate_code. Labels are owner-authored, so collisions are real. */
  slug = (label: string): string => label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/gu, ""),

  /** Core fields keep their canonical key, which is their id. Custom fields are
   *  exposed by a label slug an agent can reason about, not their Puck id. */
  formToolFields = (
    fields: FieldDef[],
    /** Property names the calling tool defines itself, so a field can't take one. */
    reserved: Iterable<string> = [],
  ): FormToolField[] => {
    const fillable = fields.filter(AGENT_FILLABLE),
      taken = new Set<string>(reserved);
    // Claimed up front: a custom field labelled "Email" must not take `email`
    // From the core field whose key that is, whichever comes first in the doc.
    for (const field of fillable) {
      if (CORE_FIELD_KEYS.has(field.id)) {
        taken.add(field.id);
      }
    }

    const out: FormToolField[] = [];
    for (const field of fillable) {
      if (CORE_FIELD_KEYS.has(field.id)) {
        out.push({ field, property: field.id });
        continue;
      }
      let property = slug(field.label) || field.id;
      if (taken.has(property)) {
        let n = 2;
        while (taken.has(`${property}_${n}`)) {
          n++;
        }
        property = `${property}_${n}`;
      }
      taken.add(property);
      out.push({ field, property });
    }
    return out;
  };

const propertySchema = (field: FieldDef): Record<string, unknown> => {
    const description = [field.label, field.helpText].filter(Boolean).join(" — ") || undefined;
    switch (field.type) {
      case "number": {
        return { description, type: "number" };
      }
      case "boolean": {
        return { description, type: "boolean" };
      }
      case "date": {
        return { description: [description, "as YYYY-MM-DD"].filter(Boolean).join(", "), type: "string" };
      }
      case "select": {
        return { description, type: "string", ...(field.options?.length ? { enum: field.options.map((o) => o.value) } : {}) };
      }
      case "multiselect": {
        return {
          description,
          items: { type: "string", ...(field.options?.length ? { enum: field.options.map((o) => o.value) } : {}) },
          type: "array",
        };
      }
      default: {
        return { description, type: "string" };
      }
    }
  },

  /** JSON Schema for the form, plus the tool's own fixed properties. Pass those
   *  same names to `formToolFields` as `reserved`, or a field will collide. */
  formInputSchema = (
    entries: FormToolField[],
    extra: Record<string, unknown> = {},
    extraRequired: string[] = [],
  ): Record<string, unknown> => {
    const properties: Record<string, unknown> = {},
      required: string[] = [...extraRequired];
    for (const { property, field } of entries) {
      properties[property] = propertySchema(field);
      if (field.required) {
        required.push(property);
      }
    }
    return { properties: { ...extra, ...properties }, type: "object", ...(required.length ? { required } : {}) };
  },

  /** Agent input → values keyed by field id, which is what the form machinery reads. */
  valuesFromInput = (
    entries: FormToolField[],
    input: Record<string, unknown>,
  ): Record<string, FieldValue> => {
    const values: Record<string, FieldValue> = {};
    for (const { property, field } of entries) {
      const raw = input[property];
      if (raw === undefined || raw === null || raw === "") {
        continue;
      }
      if (field.type === "boolean") {
        values[field.id] = raw === true || raw === "true";
      } else if (field.type === "number") {
        const n = Number(raw);
        if (!Number.isNaN(n)) {
          values[field.id] = n;
        }
      } else if (field.type === "multiselect") {
        values[field.id] = Array.isArray(raw) ? raw.map(String) : [String(raw)];
      } else {
        values[field.id] = String(raw).trim();
      }
    }
    return values;
  },

  /** Field labels for the ids a required-check rejected, for an agent-readable error. */
  labelsForIds = (fields: FieldDef[], ids: string[]): string[] => ids.map((id) => fields.find((f) => f.id === id)?.label || id);

export type { FormToolField };
export { formToolFields, formInputSchema, valuesFromInput, labelsForIds };
